/*---------- Deps, Hooks & CSS ----------*/
import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import styles from "./ExtraBadgesStatusManager.module.css"

/*---------- Services ----------*/
import * as progressService from '../../../services/progressService'
import * as clientEventService from '../../../services/clientEventService'

/*---------- Components ----------*/
import Card from "../../../components/Home/StatusSubpages/Card"
import LoadingSpinner from '../../../components/Miscellaneous/LoadingSpinner'
import ProgressSteps from '../../../components/Miscellaneous/ProgressSteps'

/*---------- Images ----------*/
import ExtraBadgesSetup from "../../../components/Home/StatusSubpages/ExtraBadgesImages/ExtraBadgesSetup.png"
import ExtraBadgeTemplateStyling from "../../../components/Home/StatusSubpages/ExtraBadgesImages/ExtraBadgeTemplateStyling.png"
import StaticFieldManager from "../../../components/Home/StatusSubpages/ExtraBadgesImages/StaticFieldManager.png"
import FieldOrdering from "../../../components/Home/StatusSubpages/ExtraBadgesImages/ExtraBadgesFieldOrdering.png"

const ExtraBadgesStatusManager = () => {

    const location = useLocation()
    const passedState = location.state
    const [clientEventInfo, setClientEventInfo] = useState({})
    const clientEventInfoRef = useRef(null);
    const [changeMade, setChangeMade] = useState(false)
    if (clientEventInfoRef === null || clientEventInfoRef.current === null) {
        if (!passedState || !passedState.clientName) {
            clientEventInfoRef.current = clientEventService.getClientEvent();
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            clientEventInfoRef.current = passedData
        }
    }
    useEffect(() => {
        // run events fetch every x seconds, if its too fast, it won't run - gets stuck
        const interval = setInterval(() => checkClientEvent(), (30 * 1000))
        return () => clearInterval(interval)
    }, [])
    const checkClientEvent = () => {
        // Update clientevent with every render when its value has changed.
        let savedClientEvent = clientEventService.getClientEvent()
        if (clientEventInfoRef.current && clientEventInfoRef.current.eventId !== undefined &&
            savedClientEvent && savedClientEvent.eventId !== undefined &&
            savedClientEvent.eventId !== clientEventInfoRef.current.eventId) {
            setClientEventInfo(savedClientEvent)
            clientEventInfoRef.current = savedClientEvent;
            setChangeMade(true)
        }
    }
    useEffect(() => {
        if (changeMade && clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined
            && clientEventInfo.eventId !== undefined && clientEventInfo.eventName !== undefined) {
            setChangeMade(false)
            fetchData()
        }

    }, [clientEventInfo])  

    /*---------- State ----------*/
    const [client, setClient] = useState(
        clientEventInfo
            ? clientEventInfo.client
            : clientEventInfo.clientId
                ? clientEventInfo.clientId
                : ""
    ) 
    const [event, setEvent] = useState(
        clientEventInfo.event
            ? clientEventInfo.event
            : clientEventInfo.eventId
                ? clientEventInfo.eventId
                : ""
    ) 
    const [clientEventObject, setClientEventObject] = useState({})
    const [loaded, setLoaded] = useState(false)

    // Required Setup
    const [extraBadgesSetupProgress, setExtraBadgesSetupProgress] = useState("Loading...")
    const [templateProgress, setTemplateProgress] = useState("Loading...")

    // Optional Setup
    const [staticFieldProgress, setStaticFieldProgress] = useState("Loading...")
    const [fieldOrderingProgress, setFieldOrderingProgress] = useState("Loading...")

    // Progress Steps
    const [requiredCompletionCount, setRequiredCompletionCount] = useState(1)
    const [optionalCompletionCount, setOptionalCompletionCount] = useState(1)

    /*---------- Constants ----------*/
    // General
    const clientName = clientEventInfo && clientEventInfo.clientName
        ? clientEventInfo.clientName
        : ""

    const eventName = clientEventInfo && clientEventInfo.eventName
            ? clientEventInfo.eventName
            : ""
            
    // curently does nothing, do we need it?
    const ERSName = clientEventInfo && clientEventInfo.eventRegistrationSystem !== undefined
        ? clientEventInfo.eventRegistrationSystem
        : "ERS"

    const clientId = clientEventInfo && clientEventInfo.clientId
        ? clientEventInfo.clientId
        : ""

    const eventId = clientEventInfo &&  clientEventInfo.eventId
            ? clientEventInfo.eventId
            : ""

    // Progress Steps 
    const requiredSteps = [
        { label: 'Extra Badges Setup', step: 1 },
        { label: 'Template Management', step: 2 },
    ]

    const optionalSteps = [
        { label: 'Static Field Mapping', step: 1 },
        { label: 'Field Ordering', step: 2 },
    ]

    /*---------- Functions ----------*/
    // Step Calculations
    const checkRequiredCompletion = () => {
        if (extraBadgesSetupProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (templateProgress === "Success") setRequiredCompletionCount(count => count + 1)
    }

    const checkOptionalCompletion = () => {
        if (staticFieldProgress === "Success") setOptionalCompletionCount(count => count + 1)
        if (fieldOrderingProgress === "Success") setOptionalCompletionCount(count => count + 1)
    }

    const checkLoaded = () => {
        if (extraBadgesSetupProgress !== "Loading..."
            && templateProgress !== "Loading..."
            && staticFieldProgress !== "Loading..."
            && fieldOrderingProgress !== "Loading..."
        )
            setLoaded(true)
    }
    const fetchData = async () => {
        const clientEventObj = {
            ClientId: clientId,
            EventId: eventId,
        }
        setClientEventObject(clientEventObj)

        const [
            extraBadgeProgress,
            templateManagementProgress,
            staticFieldStatus,
            fieldProgress,
        ] = await Promise.all([
            progressService.getExtraBadgesSetupProgress(clientEventObj),
            progressService.getTemplateManagmentProgress(clientEventObj),
            progressService.getExtraBadgesStaticFieldProgress(clientEventObj),
            progressService.getExtraBadgesFieldOrderingProgress(clientEventObj),
        ])

        setExtraBadgesSetupProgress(extraBadgeProgress)
        setTemplateProgress(templateManagementProgress)
        setStaticFieldProgress(staticFieldStatus)
        setFieldOrderingProgress(fieldProgress)
    }
    useEffect(() => {
        if (clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined) {
            fetchData()
        }
    }, [clientEventInfo])
    useEffect(() => {
        // store into local object
        if (!passedState || !passedState.clientName) {
            //let localclientevent = clientEventService.getClientEvent()
            if (clientEventInfoRef.current) {
                setClientEventInfo(clientEventInfoRef.current)
            }
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            setClientEventInfo(passedData)
            clientEventInfoRef.current = passedData;
        }
    }, [])

    useEffect(() => {
        checkLoaded()
        checkRequiredCompletion()
        checkOptionalCompletion()
    }, [fieldOrderingProgress])

    return (
        <>
            {
                loaded ?
                    <div id={styles.statusSubpage}>
                        <div className={styles.requiredSetupHeader}>
                            <h2 className={styles.requiredSetupHeading}>Required Setup</h2>
                            <ProgressSteps
                                steps={requiredSteps}
                                activeStep={requiredCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={ExtraBadgesSetup}
                                header="Extra Badge Setup"
                                content={`Map fields from ${ERSName} for ${eventName}'s extra badges`}
                                styles={styles}
                                linkName={`/ExtraBadgeTemplateManager`}
                                linkState={clientEventInfo}
                                disabledBool={false} // hardcoded as it's the first required step of an event --- can't be disabled
                            />
                            <Card
                                dataImage={ExtraBadgeTemplateStyling}
                                header="Template Management"
                                content={`Create/manage event badge templates`}
                                styles={styles}
                                linkName={`/ExtraBadgeTemplateStyling`}
                                linkState={clientEventInfo}
                                disabledBool={extraBadgesSetupProgress !== "Success" ? true : false}
                            />
                        </div>

                        <div className={styles.optionalSetupHeader}>
                            <h2 className={styles.optionalSetupHeading}>Optional Setup</h2>
                            <ProgressSteps
                                steps={optionalSteps}
                                activeStep={optionalCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={StaticFieldManager}
                                header="Static Field Mapping"
                                content={`Manage QR Code configuration for ${eventName}`}
                                styles={styles}
                                linkName={`/StaticFieldManager`}
                                linkState={clientEventInfo}
                                disabledBool={templateProgress !== "Success" ? true : false}
                            />
                            <Card
                                dataImage={FieldOrdering}
                                header="Field Ordering"
                                content={`Arrange mapped fields in order you want them to be presented on your extra badges`}
                                styles={styles}
                                linkName={`/ExtraBadgeTemplateFieldOrdering`} // There's no page for this yet
                                linkState={clientEventInfo}
                                disabledBool={templateProgress !== "Success" ? true : false}
                            />
                        </div>
                    </div>
                    :
                    <LoadingSpinner />
            }
        </>
    );
}

export default ExtraBadgesStatusManager;